import { provideHindsightIdentify } from "@/api/user";

// Starts Hindsight session capture (legalnature/hindsight) behind its runtime
// flag, `hindsight` in nuxt.config.js's publicRuntimeConfig. Even when enabled,
// the SDK records only while the Hindsight service's policy for the site key is
// on. The SDK is loaded here, in a client-only plugin, and only when enabled, so
// pages without the flag never download it and the server never imports it.
//
// It also provides $hindsightSessionId(), the session for this page's Foretell
// events: the SDK's hs.sessionId(), or null when the flag is off, the SDK fails
// to load, or no ID arrives within SESSION_ID_WAIT_MS. The SDK bounds its own
// wait, but not the time its chunk takes to load, so this deadline covers both.
//
// When enabled, it also gives api/user.js's setHindsightUser the visitor's
// identify. That calls the SDK's hs.identify() once the SDK has started and
// named the page's view, as the SDK drops calls made before init. A failure in
// the SDK is contained, so a login or page load never sees it.
//
// It also provides $hindsightExposure(arm), which records that the page showed
// an experiment arm (the SDK's hs.exposure(), with an arm such as
// services/hindsight.js's formTestExposure builds). It is delivered like the
// identify: once the SDK has named the page's view, as it was when called, and
// contained. With the flag off it does nothing.
const SESSION_ID_WAIT_MS = 2000;

export default ({ $config, app }, inject) => {
  const { enabled, siteKey, cookieDomain, releaseId } = $config.hindsight;
  if (!enabled || !siteKey) {
    inject("hindsightSessionId", () => Promise.resolve(null));
    inject("hindsightExposure", () => {});
    return;
  }
  // Loading starts with the plugin, so an early Foretell event rarely waits on
  // it; the SDK starts at onNuxtReady.
  const loading = import(
    /* webpackChunkName: "hindsight" */ "@legalnature/hindsight"
  );
  const sdk = loading.then(
    ({ hs }) => hs,
    () => null
  );
  inject("hindsightSessionId", () => {
    let timer;
    const deadline = new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), SESSION_ID_WAIT_MS);
    });
    const sessionId = sdk.then((hs) => (hs ? hs.sessionId() : null));
    return Promise.race([sessionId, deadline]).then((id) => {
      clearTimeout(timer);
      return id;
    });
  });

  let started;
  const ready = new Promise((resolve) => (started = resolve));
  provideHindsightIdentify((identity) => {
    const copy = { ...identity };
    ready.then((hs) => hs.identify(copy)).catch(() => {});
  });
  inject("hindsightExposure", (arm) => {
    if (!arm) return;
    const copy = { ...arm, testFields: { ...arm.testFields } };
    ready.then((hs) => hs.exposure(copy)).catch(() => {});
  });

  window.onNuxtReady(async () => {
    const { hs } = await loading;
    hs.init({ siteKey, releaseId, cookieDomain: cookieDomain || undefined });

    // One view per path. The wizard and checkout change steps with
    // history.pushState and replaceState outside Vue Router, so views follow
    // the history API and back/forward instead of router hooks. Vue Router's
    // own navigations go through the same history calls.
    let path;
    const startView = () => {
      if (window.location.pathname === path) return;
      path = window.location.pathname;
      hs.startView({ path, routeName: app.router.match(path).name || null });
    };
    const followHistory = (method) => {
      const original = window.history[method];
      window.history[method] = function () {
        const result = original.apply(this, arguments);
        // Never fail the navigation: Vue Router treats a throwing pushState as
        // unsupported and reloads the page.
        try {
          startView();
        } catch (e) {}
        return result;
      };
    };
    followHistory("pushState");
    followHistory("replaceState");
    window.addEventListener("popstate", startView);
    startView();
    started(hs);
  });
};
